import fetchClient, { ApiResponse } from '../api-client'
import { AddPackageToUnitePayload } from './types/AddPackageToUnitePayload'
import { ApprovePkgReqPayload } from './types/ApprovePkgReqPayload'
import { ApprovePkgReqResponse } from './types/ApprovePkgReqResponse'
import { CreatePackageReqPayload } from './types/CreatePackageReqPayload'
import { GetPkgReqsListPayload } from './types/GetPkgReqsListPayload'
import { GetPkgReqsListResponse } from './types/GetPkgReqsListResponse'
import { GetPkgReqsPayload } from './types/GetPkgReqsPayload'
import { GetPackageReqsResponse } from './types/GetPkgReqsResponse'
import { GetUnitedPkgDataResponse } from './types/GetUnitedPkgDataResponse'
import { PackageReqStatsResponse } from './types/PackageReqStats'
import { PkgReq } from './types/PkgReq'
import { PkgReqCountsResult } from './types/PkgReqCounts'
import { PackageReqFeesResponse } from './types/PkgReqFeesResponse'
import { RejectPackageReqPayload } from './types/RejectPackageReqPayload'
import { UpdatePackageReqPayload } from './types/UpdatePkgReqPayload'

/**
 * Get package requests with optional filtering
 */
export const getPackageReqs = async (params: GetPkgReqsPayload) => {
  try {
    const res = await fetchClient.post<ApiResponse<{ response: GetPackageReqsResponse }>>(`/preqs`, params)
    return res.data.data?.response || {}
  } catch (error) {
    throw error
  }
}
export const getPackageReq = async (id: string) => {
  try {
    const res = await fetchClient.get<ApiResponse<PkgReq>>(`/preqs/${id}`)
    return res.data.data
  } catch (error) {
    throw error
  }
}

/**
 * Get a paginated, server-filtered page of package requests.
 * Replaces the old grouped `getPackageReqs` for the office grid so we never
 * load the whole fulfilled history at once.
 */
export const listPackageReqs = async (payload: GetPkgReqsListPayload): Promise<GetPkgReqsListResponse> => {
  try {
    const res = await fetchClient.post<ApiResponse<GetPkgReqsListResponse>>(`/pkg-req/list`, payload)
    return res.data.data || { items: [], total: 0, page: payload.page ?? 0, pageSize: payload.pageSize ?? 0 }
  } catch (error) {
    throw error
  }
}

/**
 * Get pending package-request counts per type (cheap, no package join).
 * Used for the office landing-page cards and the navbar badge.
 */
export const getPackageReqCounts = async (warehouse?: string): Promise<PkgReqCountsResult> => {
  try {
    const res = await fetchClient.get<ApiResponse<PkgReqCountsResult>>(
      `/pkg-req/counts${warehouse ? `?warehouse=${encodeURIComponent(warehouse)}` : ''}`
    )
    return (
      res.data.data || {
        counts: {},
        specialRequestAwaitingOffice: 0,
      }
    )
  } catch (error) {
    throw error
  }
}

export const respondPackageReq = async (payload: { packageReqId: string; note: string }) => {
  const res = await fetchClient.post<ApiResponse<PkgReq>>(`/pkg-req/respond`, payload)
  if (!res.data.data) {
    throw new Error('Failed to respond to package request')
  }
  return res.data.data
}
/**
 * Get package request fees by package ID
 */
export const getPackageFeesByPackageId = async (packageId: string) => {
  try {
    const res = await fetchClient.get<ApiResponse<{ data: PackageReqFeesResponse }>>(`/preqs/package-fees/${packageId}`)
    return (
      res.data.data?.data || {
        uniteFee: 0,
        photoRequestFeeUSD: 0,
        shrinkRequestFeeUSD: 0,
      }
    )
  } catch (error) {
    throw error
  }
}

/**
 * Create a new package request
 */
export const createPackageReq = async (data: CreatePackageReqPayload) => {
  const res = await fetchClient.post<ApiResponse<PkgReq>>(`/preqs-create`, { data })
  if (!res.data.data) {
    throw new Error('Failed to create package request')
  }
  return res.data.data
}

/**
 * Reject a package request
 */
export const rejectPackageReq = async (data: RejectPackageReqPayload) => {
  const res = await fetchClient.post<ApiResponse<undefined>>(`/preqs-reject`, { data })
  return res.data
}

/**
 * Update a package request
 */
export const updatePackageReq = async (data: UpdatePackageReqPayload) => {
  const res = await fetchClient.post<ApiResponse<PkgReq>>(`/preqs-update`, { data })
  return res.data.data
}

/**
 * Add a package to an existing pending unite request
 */
export const addPackageToUniteReq = async (data: AddPackageToUnitePayload) => {
  const res = await fetchClient.post<ApiResponse<PkgReq>>(`/pkg-req/add-to-unite`, { data })
  if (!res.data.data) {
    throw new Error('Failed to add package to unite request')
  }
  return res.data.data
}

/**
 * Approve a package request
 */
export const approvePackageReq = async (packageRequest: ApprovePkgReqPayload) => {
  const res = await fetchClient.post<ApprovePkgReqResponse>(`/preqs-approve`, packageRequest)
  return res.data
}

/**
 * Get united package data for a package request
 */
export const getUnitedPackageData = async (packageReqId: string) => {
  try {
    const res = await fetchClient.get<ApiResponse<{ data: GetUnitedPkgDataResponse } | GetUnitedPkgDataResponse>>(
      `/preqs-unite/${packageReqId}`
    )
    if ('data' in res.data.data) {
      return res.data.data.data
    }
    return res.data.data
  } catch (error) {
    throw error
  }
}

/**
 * Download CSV report for all packages in pending unite requests
 */
export const downloadUnitePackagesReport = async (warehouse: string) => {
  try {
    const response = await fetchClient.get(`/preqs-unite-packages-report?warehouse=${warehouse}`, {
      responseType: 'blob',
    })
    return response.data
  } catch (error) {
    throw error
  }
}

export interface PackageReqStatsFilter {
  warehouse?: string
  startDate?: string
  endDate?: string
}

/**
 * Get package request statistics
 */
export const getPackageReqStats = async (filter?: PackageReqStatsFilter): Promise<PackageReqStatsResponse | undefined> => {
  try {
    const res = await fetchClient.post<ApiResponse<PackageReqStatsResponse>>(`/preqs/stats`, filter)
    return res.data.data
  } catch (error) {
    throw error
  }
}
